import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Popover from '@mui/material/Popover';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import { Editor, useEditorState } from '@tiptap/react';
import { FC, lazy, ReactNode, Suspense, useState } from 'react';
import {
  MdFormatAlignCenter,
  MdFormatAlignLeft,
  MdFormatAlignRight,
  MdFormatBold,
  MdFormatColorText,
  MdFormatItalic,
  MdFormatListBulleted,
  MdLink,
  MdLinkOff,
  MdOutlineEmojiEmotions,
} from 'react-icons/md';

// Lazy-load the emoji picker panel so it doesn't bloat the editor bundle.
const EmojiPicker = lazy(() => import('emoji-picker-react'));

/** Toolbar buttons `RichTextEditor` can show — see its `toolbarControls` prop. */
export type TRichTextControl =
  | 'bold'
  | 'italic'
  | 'bulletList'
  | 'link'
  | 'emoji'
  | 'alignment'
  | 'fontSize'
  | 'textColor';

const FONT_SIZES = Array.from({ length: 11 }, (_, i) => `${12 + i * 2}px`);
/** Shown as the selected size when no explicit fontSize mark is set — the editor's actual base size. */
const DEFAULT_FONT_SIZE = '14px';

const TEXT_COLORS = ['#F6E6E8', '#FFB9C8', '#FF6081', '#e7365b', '#FF073B', '#E20B38', '#85001C'];

interface ToolbarProps {
  editor: Editor | null;
  /** Extra action buttons appended after the built-in tools (e.g. the post composer's attach/poll). */
  extraActions?: ReactNode;
  /** Which built-in controls to render. */
  controls: TRichTextControl[];
}

export const RichTextToolbar: FC<ToolbarProps> = ({ editor, extraActions, controls }) => {
  const [linkAnchor, setLinkAnchor] = useState<HTMLElement | null>(null);
  const [emojiAnchor, setEmojiAnchor] = useState<HTMLElement | null>(null);
  const [textColorAnchor, setTextColorAnchor] = useState<HTMLElement | null>(null);
  const [linkUrl, setLinkUrl] = useState('');

  const editorState = useEditorState({
    editor,
    selector: ({ editor: ed }) => {
      if (!ed) {
        return {
          isBoldActive: false,
          isItalicActive: false,
          isLinkActive: false,
          isBulletListActive: false,
          canBold: false,
          canItalic: false,
          canBulletList: false,
          textAlign: 'left',
          fontSize: '',
          textColor: '',
        };
      }
      return {
        isBoldActive: ed.isActive('bold'),
        isItalicActive: ed.isActive('italic'),
        isLinkActive: ed.isActive('link'),
        isBulletListActive: ed.isActive('bulletList'),
        canBold: ed.can().chain().toggleBold().run(),
        canItalic: ed.can().chain().toggleItalic().run(),
        canBulletList: ed.can().chain().toggleBulletList().run(),
        textAlign: (ed.getAttributes('paragraph').textAlign as string | undefined) ?? 'left',
        fontSize: (ed.getAttributes('textStyle').fontSize as string | undefined) ?? '',
        textColor: (ed.getAttributes('textStyle').color as string | undefined) ?? '',
      };
    },
  });

  if (!editor || !editorState) {
    return null;
  }

  const has = (control: TRichTextControl) => controls.includes(control);

  const openLinkPopover = (target: HTMLElement) => {
    setLinkUrl((editor.getAttributes('link').href as string | undefined) ?? '');
    setLinkAnchor(target);
  };

  const closeLinkPopover = () => setLinkAnchor(null);

  const applyLink = () => {
    const url = linkUrl.trim();
    if (!url) {
      removeLink();
      return;
    }

    const { from, to } = editor.state.selection;
    const isCollapsed = from === to;
    if (isCollapsed && !editor.isActive('link')) {
      // No text selected and not editing an existing link: `setLink` would have
      // nothing to mark, so insert the URL itself as the linked text.
      editor
        .chain()
        .focus()
        .insertContent({ type: 'text', text: url, marks: [{ type: 'link', attrs: { href: url } }] })
        .run();
    } else {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
    closeLinkPopover();
  };

  const removeLink = () => {
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
    closeLinkPopover();
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 0.5,
        px: 1,
        py: 0.5,
        borderBottom: theme => `1px solid ${theme.palette.divider}`,
      }}
    >
      {has('bold') && (
        <Tooltip title="Bold">
          <span>
            <IconButton
              size="small"
              onMouseDown={e => {
                e.preventDefault();
                editor.chain().focus().toggleBold().run();
              }}
              disabled={!editorState.canBold}
              color={editorState.isBoldActive ? 'primary' : 'default'}
              aria-label="Bold"
            >
              <MdFormatBold size={18} />
            </IconButton>
          </span>
        </Tooltip>
      )}
      {has('italic') && (
        <Tooltip title="Italic">
          <span>
            <IconButton
              size="small"
              onMouseDown={e => {
                e.preventDefault();
                editor.chain().focus().toggleItalic().run();
              }}
              disabled={!editorState.canItalic}
              color={editorState.isItalicActive ? 'primary' : 'default'}
              aria-label="Italic"
            >
              <MdFormatItalic size={18} />
            </IconButton>
          </span>
        </Tooltip>
      )}
      {has('bulletList') && (
        <Tooltip title="Bullet list">
          <span>
            <IconButton
              size="small"
              onMouseDown={e => {
                e.preventDefault();
                editor.chain().focus().toggleBulletList().run();
              }}
              disabled={!editorState.canBulletList}
              color={editorState.isBulletListActive ? 'primary' : 'default'}
              aria-label="Bullet list"
            >
              <MdFormatListBulleted size={18} />
            </IconButton>
          </span>
        </Tooltip>
      )}
      {has('alignment') && (
        <>
          <Tooltip title="Align left">
            <span>
              <IconButton
                size="small"
                onMouseDown={e => {
                  e.preventDefault();
                  editor.chain().focus().setTextAlign('left').run();
                }}
                color={editorState.textAlign === 'left' ? 'primary' : 'default'}
                aria-label="Align left"
              >
                <MdFormatAlignLeft size={18} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Align center">
            <span>
              <IconButton
                size="small"
                onMouseDown={e => {
                  e.preventDefault();
                  editor.chain().focus().setTextAlign('center').run();
                }}
                color={editorState.textAlign === 'center' ? 'primary' : 'default'}
                aria-label="Align center"
              >
                <MdFormatAlignCenter size={18} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Align right">
            <span>
              <IconButton
                size="small"
                onMouseDown={e => {
                  e.preventDefault();
                  editor.chain().focus().setTextAlign('right').run();
                }}
                color={editorState.textAlign === 'right' ? 'primary' : 'default'}
                aria-label="Align right"
              >
                <MdFormatAlignRight size={18} />
              </IconButton>
            </span>
          </Tooltip>
        </>
      )}
      {has('fontSize') && (
        <TextField
          size="small"
          select
          value={editorState.fontSize || DEFAULT_FONT_SIZE}
          onChange={e => {
            const size = e.target.value;
            if (size === DEFAULT_FONT_SIZE) {
              editor.chain().focus().unsetFontSize().run();
            } else {
              editor.chain().focus().setFontSize(size).run();
            }
          }}
          sx={{
            width: 75,
            '& .MuiInputBase-root': { height: 30 },
            '& .MuiSelect-select': { py: 0, pr: '20px !important', fontSize: 13 },
          }}
        >
          {FONT_SIZES.map(size => (
            <MenuItem key={size} value={size} sx={{ fontSize: 13 }}>
              {size}
            </MenuItem>
          ))}
        </TextField>
      )}
      {has('textColor') && (
        <Tooltip title="Text color">
          <span>
            <IconButton
              size="small"
              onMouseDown={e => {
                e.preventDefault();
                setTextColorAnchor(e.currentTarget);
              }}
              aria-label="Text color"
            >
              <MdFormatColorText
                size={18}
                style={editorState.textColor ? { color: editorState.textColor } : undefined}
              />
            </IconButton>
          </span>
        </Tooltip>
      )}
      {has('link') && (
        <Tooltip title="Link">
          <span>
            <IconButton
              size="small"
              onMouseDown={e => {
                e.preventDefault();
                openLinkPopover(e.currentTarget);
              }}
              color={editorState.isLinkActive ? 'primary' : 'default'}
              aria-label="Link"
            >
              <MdLink size={18} />
            </IconButton>
          </span>
        </Tooltip>
      )}
      {has('emoji') && (
        <Tooltip title="Emoji">
          <span>
            <IconButton
              size="small"
              onMouseDown={e => {
                e.preventDefault();
                setEmojiAnchor(e.currentTarget);
              }}
              color="default"
              aria-label="Emoji"
            >
              <MdOutlineEmojiEmotions size={18} />
            </IconButton>
          </span>
        </Tooltip>
      )}
      {extraActions}

      <Popover
        open={Boolean(linkAnchor)}
        anchorEl={linkAnchor}
        onClose={closeLinkPopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ display: 'flex', gap: 1, p: 1.5, alignItems: 'center' }}>
          <TextField
            size="small"
            autoFocus
            placeholder="https://example.com"
            value={linkUrl}
            onChange={e => setLinkUrl(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                applyLink();
              }
            }}
            sx={{ minWidth: 240 }}
          />
          <Button size="small" variant="contained" onClick={applyLink} disabled={!linkUrl.trim()}>
            Apply
          </Button>
          {editorState.isLinkActive && (
            <Button
              size="small"
              variant="outlined"
              color="error"
              onClick={removeLink}
              startIcon={<MdLinkOff size={16} />}
            >
              Remove
            </Button>
          )}
        </Box>
      </Popover>

      <Popover
        open={Boolean(emojiAnchor)}
        anchorEl={emojiAnchor}
        onClose={() => setEmojiAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Suspense
          fallback={
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3, width: 300 }}>
              <CircularProgress size={20} />
            </Box>
          }
        >
          <EmojiPicker
            onEmojiClick={emoji => {
              editor.chain().focus().insertContent(emoji.emoji).run();
              setEmojiAnchor(null);
            }}
          />
        </Suspense>
      </Popover>

      <Popover
        open={Boolean(textColorAnchor)}
        anchorEl={textColorAnchor}
        onClose={() => setTextColorAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ display: 'flex', gap: 0.75, p: 1.5 }}>
          {TEXT_COLORS.map(color => (
            <Tooltip key={color} title={color}>
              <Box
                component="button"
                type="button"
                onMouseDown={e => e.preventDefault()}
                onClick={() => {
                  editor.chain().focus().setTextColor(color).run();
                  setTextColorAnchor(null);
                }}
                sx={{
                  width: 22,
                  height: 22,
                  p: 0,
                  border: 2,
                  borderColor: editorState.textColor.toLowerCase() === color.toLowerCase() ? 'text.primary' : 'divider',
                  borderRadius: 0.5,
                  bgcolor: color,
                  cursor: 'pointer',
                }}
              />
            </Tooltip>
          ))}
        </Box>
      </Popover>
    </Box>
  );
};
